import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { match } from 'ts-pattern';
import {
  Contact,
  MutationCreateContactArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactFormValues } from 'src/components/domain/contacts/contact-form/contact-form.schema';
import {
  invalidPhoneNumberSchema,
  PossibleDuplicate,
  possibleDuplicateSchema,
} from 'src/components/domain/contacts/contact-form/possible-duplicate-contact-modal';
import { GET_CONTACT } from 'src/hooks/gql/use-refresh-contact';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export type PossibleDuplicateContactState = {
  duplicateData: PossibleDuplicate;
  contact: Contact | undefined;
  // Partial because when this originates from the update flow (autosave or
  // manual update), `input` is only a subset of CreateContactInput's fields.
  queryVariables: Omit<MutationCreateContactArgs, 'input'> & {
    input: Partial<MutationCreateContactArgs['input']>;
  };
};

type UseContactUpsertErrorHandlerParams = {
  form: UseFormReturn<ContactFormValues>;
  onPossibleDuplicate: (state: PossibleDuplicateContactState) => void;
  // Whether the field that conflicts with the possible duplicate gets flagged with an error.
  markConflictField: boolean;
};

export const useContactUpsertErrorHandler = ({
  form,
  onPossibleDuplicate,
  markConflictField,
}: UseContactUpsertErrorHandlerParams) => {
  const { t } = useTranslation(['common']);
  const handleGraphQLError = useHandleGraphQLError();
  const [getContact] = useLazyQuery(GET_CONTACT);

  const getConflictField = useCallback(
    (duplicateContact: PossibleDuplicate) => {
      if (duplicateContact.conflictField.type === 'EMAIL') {
        return 'primaryEmail';
      }

      const { primaryPhone, secondaryPhone } = form.getValues();

      if (duplicateContact.conflictField.value === primaryPhone) {
        return 'primaryPhone';
      }

      if (duplicateContact.conflictField.value === secondaryPhone) {
        return 'secondaryPhone';
      }

      return null;
    },
    [form],
  );

  const handleUpsertError = useCallback(
    async (
      error: ApolloError,
      queryVariables: PossibleDuplicateContactState['queryVariables'],
    ) => {
      const errorToHandle = error.graphQLErrors?.[0];

      await match(errorToHandle)
        .with(
          {
            extensions: {
              rawCode: 'POSSIBLE_DUPLICATE_CONTACT',
            },
          },
          async (errorToHandle) => {
            const duplicateContact = possibleDuplicateSchema.parse(
              errorToHandle.extensions.detail,
            );

            const contact = await getContact({
              variables: {
                id: duplicateContact.id,
              },
            })
              .then(({ data }) => data?.contact)
              .catch(() => undefined);

            if (markConflictField) {
              const conflictField = getConflictField(duplicateContact);

              if (conflictField) {
                form.setError(conflictField, {
                  message: ' ',
                  type: 'value',
                });
              }
            }

            onPossibleDuplicate({
              duplicateData: duplicateContact,
              contact,
              queryVariables,
            });
          },
        )
        .with(
          {
            extensions: {
              code: 'BAD_PARAMS:INPUT_VALIDATION',
            },
          },
          (errorToHandle) => {
            const invalidPhoneNumber = invalidPhoneNumberSchema.parse(
              errorToHandle.extensions.detail[0],
            );

            const errorPayload = {
              message: t('common:validations.invalidPhoneNumber'),
              type: 'value',
            };

            if (invalidPhoneNumber.property === 'phone_cel') {
              form.setError('primaryPhone', errorPayload);
            }

            if (invalidPhoneNumber.property === 'phone_home') {
              form.setError('secondaryPhone', errorPayload);
            }
          },
        )
        .otherwise(() => {
          handleGraphQLError(error);
        });
    },
    [
      form,
      getConflictField,
      getContact,
      handleGraphQLError,
      markConflictField,
      onPossibleDuplicate,
      t,
    ],
  );

  return { handleUpsertError, getConflictField };
};

export const getContactUpsertVariables = <T extends Partial<ContactFormValues>>(
  values: T,
) => {
  const { address, tags, ...rest } = values;

  return {
    input: {
      ...rest,
      ...address,
      tags: tags?.map(String),
    },
  };
};
